import { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Clock, Send } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, Eyebrow } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SITE_TYPES = [
  { id: "starter", label: "The Starter Workshop — 1-page site", base: 349 },
  { id: "mainstreet", label: "Main Street Standard — up to 4 pages", base: 599 },
  { id: "businessplus", label: "Business Plus — up to 6 pages", base: 849 },
  { id: "custom", label: "Custom build (booking / integrations)", base: 1000 },
];

import { ADDONS as EXTRAS } from "@/data/addons";

const Contact = () => {
  const [siteType, setSiteType] = useState("mainstreet");
  const [extras, setExtras] = useState([]);
  const [rush, setRush] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: "", business: "", email: "", phone: "", message: "" });

  const estimate = useMemo(() => {
    const base = SITE_TYPES.find((t) => t.id === siteType).base + extras.reduce((sum, id) => sum + EXTRAS.find((e) => e.id === id).cost, 0);
    const multiplier = rush ? 1.2 : 1;
    const low = Math.round((base * multiplier) / 25) * 25;
    const high = Math.round((base * multiplier * 1.15) / 25) * 25;
    return { low, high };
  }, [siteType, extras, rush]);

  const toggleExtra = (id) => setExtras((prev) => (prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id]));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/quotes`, {
        ...form,
        service_type: SITE_TYPES.find((t) => t.id === siteType).label,
        estimate: `$${estimate.low.toLocaleString()}–$${estimate.high.toLocaleString()}${rush ? " (rush)" : ""}`,
      });
      setSent(true);
      toast.success("Your request is in. We'll be in touch soon.");
    } catch (err) {
      toast.error("Something went wrong sending that. Please email us directly at goodworkweb1@gmail.com.");
    } finally {
      setSending(false);
    }
  };

  const [cForm, setCForm] = useState({ name: "", email: "", message: "" });
  const [cSending, setCSending] = useState(false);
  const [cSent, setCSent] = useState(false);

  const submitQuick = async (e) => {
    e.preventDefault();
    setCSending(true);
    try {
      await axios.post(`${API}/quotes`, { ...cForm, business: "", phone: "", service_type: "Quick contact form", estimate: "" });
      setCSent(true);
      toast.success("Message sent — we'll be in touch soon.");
    } catch {
      toast.error("Something went wrong. Please email us directly at goodworkweb1@gmail.com.");
    } finally {
      setCSending(false);
    }
  };

  const inputCls = "w-full bg-white border border-[#E2DACB] px-4 py-3 text-sm text-[#233D35] placeholder-[#77786F]/60 focus:border-[#C58A47] focus:outline-none transition-colors";

  return (
    <div data-testid="contact-page">
      <Seo
        title="Get a Free Quote | Good Work Web Co."
        description="Tell us about your business and get a free, no-pressure quote for a professional website — serving the Almaguin Highlands and Northern Ontario."
      />

      <section className="py-12 sm:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Eyebrow>Get a Free Quote</Eyebrow>
            <h1 className="font-head font-bold uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl text-[#233D35] mt-3 leading-[0.95] max-w-3xl" data-testid="contact-heading">
              Tell us about your work.
            </h1>
            <p className="mt-5 max-w-2xl text-[#77786F] text-base sm:text-lg leading-relaxed">
              Pick what you need below for an instant ballpark, then send it over. We'll get back to you with a straight answer and a fair price.
            </p>
          </Reveal>

          <div className="mt-12 grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Calculator + form */}
            <Reveal className="lg:col-span-3">
              <div className="bg-white border border-[#E2DACB] shadow-[0_4px_20px_rgba(35,61,53,0.06)] p-6 sm:p-8">
                {sent ? (
                  <div className="text-center py-16" data-testid="quote-success">
                    <span className="font-head font-bold uppercase tracking-tight text-3xl text-[#233D35] block">Thank you.</span>
                    <p className="mt-3 text-[#77786F] max-w-md mx-auto">
                      Your quote request is in — a confirmation is on its way to your inbox. We'll review everything and get back to you shortly, usually within a couple of business days.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} data-testid="quote-form">
                    <h2 className="font-head font-bold uppercase tracking-tight text-xl text-[#233D35]">1 · What kind of site do you need?</h2>
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {SITE_TYPES.map((t) => (
                        <button
                          type="button"
                          key={t.id}
                          onClick={() => setSiteType(t.id)}
                          data-testid={`site-type-${t.id}`}
                          aria-pressed={siteType === t.id}
                          className={`text-left p-4 border transition-colors duration-200 ${
                            siteType === t.id ? "border-[#233D35] bg-[#233D35] text-[#F3EFE5]" : "border-[#E2DACB] hover:border-[#233D35]"
                          }`}
                        >
                          <span className="block text-sm font-semibold leading-snug">{t.label}</span>
                          <span className={`block mt-2 font-head font-bold text-lg ${siteType === t.id ? "text-[#C58A47]" : "text-[#C58A47]"}`}>
                            from ${t.base.toLocaleString()}
                          </span>
                        </button>
                      ))}
                    </div>

                    <h2 className="font-head font-bold uppercase tracking-tight text-xl text-[#233D35] mt-8">2 · Any extras?</h2>
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {EXTRAS.map((x) => (
                        <label
                          key={x.id}
                          className={`flex items-center gap-3 p-3.5 border cursor-pointer transition-colors duration-200 ${
                            extras.includes(x.id) ? "border-[#C58A47] bg-[#C58A47]/5" : "border-[#E2DACB] hover:border-[#C58A47]"
                          }`}
                          data-testid={`extra-${x.id}`}
                        >
                          <input
                            type="checkbox"
                            checked={extras.includes(x.id)}
                            onChange={() => toggleExtra(x.id)}
                            className="w-4 h-4 accent-[#C58A47]"
                          />
                          <span className="text-sm text-[#233D35] flex-1">{x.label}</span>
                          <span className="text-sm font-semibold text-[#C58A47]">+${x.cost}</span>
                        </label>
                      ))}
                    </div>

                    <label className="mt-4 flex items-center gap-3 p-3.5 border border-[#E2DACB] cursor-pointer hover:border-[#C58A47] transition-colors" data-testid="rush-toggle">
                      <input type="checkbox" checked={rush} onChange={() => setRush(!rush)} className="w-4 h-4 accent-[#C58A47]" />
                      <span className="text-sm text-[#233D35] flex-1">I need it fast (rush timeline)</span>
                      <span className="text-sm font-semibold text-[#C58A47]">+20%</span>
                    </label>

                    <div className="mt-6 bg-[#EBE5D8] border border-[#E2DACB] p-5 flex items-center justify-between" data-testid="estimate-display">
                      <span className="font-head font-bold uppercase tracking-[0.14em] text-sm text-[#314F64]">Your ballpark</span>
                      <span className="font-head font-bold text-3xl text-[#233D35]">
                        ${estimate.low.toLocaleString()}–${estimate.high.toLocaleString()}
                      </span>
                    </div>

                    <h2 className="font-head font-bold uppercase tracking-tight text-xl text-[#233D35] mt-8">3 · Your details</h2>
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="q-name" className="block text-sm font-semibold text-[#233D35] mb-1.5">Name *</label>
                        <input id="q-name" required data-testid="quote-name" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Doe" />
                      </div>
                      <div>
                        <label htmlFor="q-business" className="block text-sm font-semibold text-[#233D35] mb-1.5">Business name</label>
                        <input id="q-business" data-testid="quote-business" className={inputCls} value={form.business} onChange={(e) => setForm({ ...form, business: e.target.value })} placeholder="Doe Contracting" />
                      </div>
                      <div>
                        <label htmlFor="q-email" className="block text-sm font-semibold text-[#233D35] mb-1.5">Email *</label>
                        <input id="q-email" type="email" required data-testid="quote-email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jane@example.com" />
                      </div>
                      <div>
                        <label htmlFor="q-phone" className="block text-sm font-semibold text-[#233D35] mb-1.5">Phone</label>
                        <input id="q-phone" type="tel" data-testid="quote-phone" className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="705-555-0123" />
                      </div>
                    </div>
                    <div className="mt-4">
                      <label htmlFor="q-message" className="block text-sm font-semibold text-[#233D35] mb-1.5">Tell us about the job *</label>
                      <textarea id="q-message" required rows={4} data-testid="quote-message" className={inputCls} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="What does your business do, and what should the website help with?" />
                    </div>

                    <button
                      type="submit"
                      disabled={sending}
                      data-testid="quote-submit"
                      className="group mt-6 inline-flex items-center gap-2 bg-[#C58A47] hover:bg-[#B0793A] disabled:opacity-60 text-[#F3EFE5] font-head font-bold uppercase tracking-[0.12em] px-8 py-4 transition-colors duration-200"
                    >
                      {sending ? "Sending…" : "Send My Request"}
                      <Send size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
                    </button>
                  </form>
                )}
              </div>
            </Reveal>

            {/* Contact info */}
            <Reveal delay={0.1} className="lg:col-span-2">
              <div className="bg-[#233D35] text-[#F3EFE5] p-6 sm:p-8 h-full grain relative" data-testid="contact-info-card">
                <div className="relative z-[2]">
                  <h2 className="font-head font-bold uppercase tracking-tight text-2xl">Prefer to just reach out?</h2>
                  <p className="mt-3 text-[#F3EFE5]/75 text-sm leading-relaxed">
                    Skip the estimator — send a quick note below, or email us directly. We're around.
                  </p>
                  <ul className="mt-8 space-y-5">
                    <li className="flex items-start gap-3">
                      <Mail size={18} className="text-[#C58A47] shrink-0 mt-0.5" />
                      <div>
                        <span className="block font-head font-bold uppercase tracking-[0.16em] text-xs text-[#F3EFE5]/60">Email</span>
                        <a href="mailto:goodworkweb1@gmail.com" className="text-sm hover:text-[#C58A47] transition-colors" data-testid="contact-email">
                          goodworkweb1@gmail.com
                        </a>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <MapPin size={18} className="text-[#C58A47] shrink-0 mt-0.5" />
                      <div>
                        <span className="block font-head font-bold uppercase tracking-[0.16em] text-xs text-[#F3EFE5]/60">Based in</span>
                        <span className="text-sm">Almaguin Highlands, Northern Ontario</span>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <Clock size={18} className="text-[#C58A47] shrink-0 mt-0.5" />
                      <div>
                        <span className="block font-head font-bold uppercase tracking-[0.16em] text-xs text-[#F3EFE5]/60">We reply</span>
                        <span className="text-sm">Usually within a couple of business days</span>
                      </div>
                    </li>
                  </ul>

                  {cSent ? (
                    <p className="mt-8 text-sm text-[#F3EFE5]/90 border border-[#C58A47]/60 p-4" data-testid="quick-contact-success">
                      Thanks — your message is in. We'll be in touch soon.
                    </p>
                  ) : (
                    <form onSubmit={submitQuick} className="mt-8 space-y-3" data-testid="quick-contact-form">
                      <input required aria-label="Your name" placeholder="Your name" data-testid="quick-name"
                        className="w-full bg-[#F3EFE5] border border-transparent px-4 py-3 text-sm text-[#233D35] placeholder-[#77786F] focus:outline-none focus:border-[#C58A47] transition-colors"
                        value={cForm.name} onChange={(e) => setCForm({ ...cForm, name: e.target.value })} />
                      <input required type="email" aria-label="Your email" placeholder="Your email" data-testid="quick-email"
                        className="w-full bg-[#F3EFE5] border border-transparent px-4 py-3 text-sm text-[#233D35] placeholder-[#77786F] focus:outline-none focus:border-[#C58A47] transition-colors"
                        value={cForm.email} onChange={(e) => setCForm({ ...cForm, email: e.target.value })} />
                      <textarea required rows={3} aria-label="Your message" placeholder="Your message" data-testid="quick-message"
                        className="w-full bg-[#F3EFE5] border border-transparent px-4 py-3 text-sm text-[#233D35] placeholder-[#77786F] focus:outline-none focus:border-[#C58A47] transition-colors"
                        value={cForm.message} onChange={(e) => setCForm({ ...cForm, message: e.target.value })} />
                      <button type="submit" disabled={cSending} data-testid="quick-submit"
                        className="inline-flex items-center gap-2 bg-[#C58A47] hover:bg-[#B0793A] disabled:opacity-60 text-[#F3EFE5] font-head font-bold uppercase tracking-[0.12em] text-sm px-6 py-3 transition-colors duration-200">
                        {cSending ? "Sending…" : "Send Message"}
                        <Send size={15} />
                      </button>
                    </form>
                  )}

                  <div className="mt-10 pt-6 border-t border-[#F3EFE5]/15">
                    <p className="font-head font-bold uppercase tracking-tight text-xl leading-tight">
                      Good websites for people doing good work.
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
